if (!customElements.get("ecomhard-kit-selector")) {
  class EcomhardKitSelector extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isSubmitting = false;
      this.isInitialized = false;
      this.slides = [];
      this.galleryRequestId = 0;
      this.galleryAbortController = null;
      this.galleryCache = new Map();
      this.currentGalleryProductId = null;
      this.currentGalleryContextKey = null;

      this.onChange = this.onChange.bind(this);
      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onAddAll = this.onAddAll.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      this.cacheElements();
      this.createSlides();

      if (!this.slides.length) return;

      this.addEventListener("change", this.onChange);
      this.previousButton?.addEventListener("click", this.onPrevious);
      this.nextButton?.addEventListener("click", this.onNext);
      this.addAllButton?.addEventListener("click", this.onAddAll);

      this.slides.forEach((slide) => {
        slide.candidates.forEach((candidate) => {
          this.syncAvailability(candidate);
        });
        this.updateSlideDisplay(slide);
      });

      this.goTo(0, { scroll: false, syncGallery: true });
      this.updateTotal();
    }

    disconnectedCallback() {
      this.removeEventListener("change", this.onChange);
      this.previousButton?.removeEventListener("click", this.onPrevious);
      this.nextButton?.removeEventListener("click", this.onNext);
      this.addAllButton?.removeEventListener("click", this.onAddAll);
      this.galleryAbortController?.abort();
      this.galleryAbortController = null;
      this.isInitialized = false;
    }

    cacheElements() {
      this.counter = this.querySelector("[data-kit-counter]");
      this.previousButton = this.querySelector("[data-kit-previous]");
      this.nextButton = this.querySelector("[data-kit-next]");
      this.addAllButton = this.querySelector("[data-kit-add-all]");
      this.totalElement = this.querySelector("[data-kit-total]");
      this.subtotalElement = this.querySelector("[data-kit-subtotal]");
      this.discountLabelElement = this.querySelector("[data-kit-discount-label]");
      this.errorElement = this.querySelector("[data-kit-error]");
      this.cartDrawer = document.querySelector("cart-drawer");
      this.cartNotification = document.querySelector("cart-notification");
      this.cartSurface = this.cartDrawer || this.cartNotification;
      this.mainCart = document.querySelector("cart-items");

      const currentGallery = document.getElementById(`MediaGallery-${this.dataset.sectionId}`);
      this.ensureGalleryColumnLayout(currentGallery);
      this.currentGalleryProductId = String(
        currentGallery?.dataset.productId || this.dataset.pageProductId || ""
      );

      this.currentGalleryContextKey = null;
    }

    ensureGalleryColumnLayout(galleryElement) {
      if (!galleryElement) return;

      const productGrid = galleryElement.closest(".product");
      const mediaWrapper = galleryElement.closest(".product__media-wrapper");

      productGrid?.classList.remove("product--no-media");
      productGrid?.classList.add("grid--2-col-tablet");
      mediaWrapper?.classList.remove("hidden");
    }

    createSlides() {
      const slideElements = Array.from(this.querySelectorAll("[data-kit-slide]"));

      this.slides = slideElements.map((element, index) => {
        const productChoiceInputs = Array.from(
          element.querySelectorAll("[data-kit-product-choice]")
        );

        const candidateElements = Array.from(
          element.querySelectorAll("[data-kit-product-candidate]")
        );

        const candidates = candidateElements.map((candidateElement) => {
          const productJson = candidateElement.querySelector("[data-kit-product-json]");
          let product = {};

          try {
            product = JSON.parse(productJson?.textContent || "{}");
          } catch (error) {
            console.error(
              "[ECOMHARD Kits] Não foi possível ler os dados de um produto do conjunto.",
              error
            );
          }

          const variants = Array.isArray(product.variants) ? product.variants : [];
          const inputs = Array.from(candidateElement.querySelectorAll("[data-kit-option]"));
          const hasOnlyDefaultVariant =
            candidateElement.dataset.hasOnlyDefaultVariant === "true" ||
            (variants.length === 1 && this.normalize(variants[0]?.title) === "default title");
          const maxOptions = hasOnlyDefaultVariant
            ? 0
            : Number(product.options?.length || variants[0]?.options?.length || 0);
          const defaultVariant = hasOnlyDefaultVariant
            ? variants.find((variant) => variant.available) || variants[0] || null
            : null;

          return {
            id: String(candidateElement.dataset.productId || product.id || ""),
            url:
              candidateElement.dataset.productUrl ||
              product.url ||
              (product.handle ? `/products/${product.handle}` : ""),
            handle: candidateElement.dataset.productHandle || product.handle || "",
            element: candidateElement,
            product,
            variants,
            inputs,
            maxOptions,
            defaultVariant,
            selections: new Map(),
            selectedValueElements: Array.from(
              candidateElement.querySelectorAll("[data-kit-selected-value]")
            ),
          };
        });

        let selectedProductId =
          productChoiceInputs.find((input) => input.checked && !input.disabled)?.dataset.productId ||
          null;

        if (!selectedProductId) {
          const firstAvailableChoice = productChoiceInputs.find((input) => !input.disabled);
          const firstAvailableCandidate = candidates.find(
            (candidate) =>
              String(candidate.id) === String(firstAvailableChoice?.dataset.productId || "")
          );

          if (firstAvailableChoice && firstAvailableCandidate) {
            firstAvailableChoice.checked = true;
            selectedProductId = firstAvailableCandidate.id;
          }
        }

        return {
          index,
          element,
          candidates,
          productChoiceInputs,
          selectedProductId: selectedProductId ? String(selectedProductId) : null,
          productTitleElement: element.querySelector("[data-kit-product-title]"),
          selectedProductLabelElement: element.querySelector(
            "[data-kit-selected-product-label]"
          ),
          priceElement: element.querySelector("[data-kit-price]"),
          comparePriceElement: element.querySelector("[data-kit-compare-price]"),
          imageElement: element.querySelector("[data-kit-image]"),
          imagePlaceholder: element.querySelector("[data-kit-image-placeholder]"),
        };
      });
    }

    normalize(value) {
      return String(value ?? "").trim().toLocaleLowerCase("pt-BR");
    }

    getVariantOptionValue(variant, position) {
      if (Array.isArray(variant?.options)) {
        return variant.options[Number(position) - 1] ?? "";
      }

      return variant?.[`option${position}`] ?? "";
    }

    getActiveCandidate(slide) {
      if (!slide?.selectedProductId) return null;

      return (
        slide.candidates.find(
          (candidate) => String(candidate.id) === String(slide.selectedProductId)
        ) || null
      );
    }

    getPreviewCandidate(slide) {
      return (
        this.getActiveCandidate(slide) ||
        slide?.candidates.find((candidate) =>
          candidate.variants.some((variant) => variant.available)
        ) ||
        slide?.candidates[0] ||
        null
      );
    }

    getSelectedVariantFromCandidate(candidate) {
      if (!candidate) return null;
      if (candidate.defaultVariant) {
        return candidate.defaultVariant.available === false ? null : candidate.defaultVariant;
      }

      if (!candidate.maxOptions || candidate.selections.size !== candidate.maxOptions) {
        return null;
      }

      return (
        candidate.variants.find((variant) => {
          if (!variant.available) return false;

          for (let position = 1; position <= candidate.maxOptions; position += 1) {
            const selectedValue = candidate.selections.get(String(position));

            if (
              this.normalize(this.getVariantOptionValue(variant, position)) !==
              this.normalize(selectedValue)
            ) {
              return false;
            }
          }

          return true;
        }) || null
      );
    }

    getDisplayVariantFromCandidate(candidate) {
      if (!candidate) return null;

      return (
        this.getSelectedVariantFromCandidate(candidate) ||
        candidate.variants.find((variant) => variant.available) ||
        candidate.variants[0] ||
        null
      );
    }

    getSelectedVariant(slide) {
      const candidate = this.getActiveCandidate(slide);
      return this.getSelectedVariantFromCandidate(candidate);
    }

    getDisplayVariant(slide) {
      return this.getDisplayVariantFromCandidate(this.getPreviewCandidate(slide));
    }

    canSelectOption(candidate, position, value) {
      const proposedSelection = new Map(candidate.selections);
      proposedSelection.set(String(position), value);

      return candidate.variants.some((variant) => {
        if (!variant.available) return false;

        for (const [selectedPosition, selectedValue] of proposedSelection.entries()) {
          if (
            this.normalize(this.getVariantOptionValue(variant, selectedPosition)) !==
            this.normalize(selectedValue)
          ) {
            return false;
          }
        }

        return true;
      });
    }

    hasAvailableVariantForSelections(candidate) {
      if (!candidate.selections.size) {
        return candidate.variants.some((variant) => variant.available);
      }

      return candidate.variants.some((variant) => {
        if (!variant.available) return false;

        for (const [position, value] of candidate.selections.entries()) {
          if (
            this.normalize(this.getVariantOptionValue(variant, position)) !==
            this.normalize(value)
          ) {
            return false;
          }
        }

        return true;
      });
    }

    reconcileSelections(candidate, changedPosition) {
      if (this.hasAvailableVariantForSelections(candidate)) return;

      const removablePositions = Array.from(candidate.selections.keys())
        .filter((position) => String(position) !== String(changedPosition))
        .sort((a, b) => Number(b) - Number(a));

      for (const position of removablePositions) {
        candidate.selections.delete(String(position));
        candidate.inputs
          .filter((input) => String(input.dataset.optionPosition) === String(position))
          .forEach((input) => {
            input.checked = false;
          });

        if (this.hasAvailableVariantForSelections(candidate)) break;
      }
    }

    syncAvailability(candidate) {
      if (!candidate) return;

      candidate.inputs.forEach((input) => {
        const position = input.dataset.optionPosition;
        const value = input.dataset.optionValue;
        const available = this.canSelectOption(candidate, position, value);
        const isSelected =
          this.normalize(candidate.selections.get(String(position))) ===
          this.normalize(value);
        const option = input.closest(".eh-kit__option");

        input.disabled = !available && !isSelected;
        option?.classList.toggle("is-unavailable", !available);
        option?.setAttribute("aria-disabled", String(!available));
      });

      candidate.selectedValueElements.forEach((element) => {
        const position = String(element.dataset.kitSelectedValue);
        element.textContent = candidate.selections.get(position) || "";
      });
    }

    syncProductChoices(slide) {
      slide.productChoiceInputs.forEach((input) => {
        const candidate = slide.candidates.find(
          (item) => String(item.id) === String(input.dataset.productId)
        );
        const available = Boolean(
          candidate?.variants.some((variant) => variant.available)
        );
        const option = input.closest(".eh-kit__option");

        input.disabled = !available;
        option?.classList.toggle("is-unavailable", !available);
        option?.setAttribute("aria-disabled", String(!available));
      });
    }

    getImageSource(image) {
      if (!image) return "";
      if (typeof image === "string") return image;
      return image.src || image.url || "";
    }

    ensureImageElement(slide) {
      if (slide.imageElement) return slide.imageElement;

      const wrapper = slide.element.querySelector(".eh-kit__image-wrap");
      if (!wrapper) return null;

      const image = document.createElement("img");
      image.className = "eh-kit__image";
      image.dataset.kitImage = "";
      image.width = 180;
      image.height = 180;
      image.loading = "lazy";
      wrapper.replaceChildren(image);

      slide.imageElement = image;
      slide.imagePlaceholder = null;
      return image;
    }

    updateSlideDisplay(slide) {
      this.syncProductChoices(slide);

      const activeCandidate = this.getActiveCandidate(slide);
      const displayCandidate = activeCandidate || this.getPreviewCandidate(slide);
      const variant = this.getDisplayVariantFromCandidate(displayCandidate);

      slide.candidates.forEach((candidate) => {
        candidate.element.hidden = !activeCandidate || candidate !== activeCandidate;
      });

      const selectedChoice = slide.productChoiceInputs.find(
        (input) =>
          input.checked &&
          String(input.dataset.productId) === String(slide.selectedProductId)
      );

      if (slide.selectedProductLabelElement) {
        slide.selectedProductLabelElement.textContent = selectedChoice?.dataset.productLabel || "";
      }

      if (slide.productTitleElement) {
        slide.productTitleElement.textContent = activeCandidate
          ? activeCandidate.product.title || ""
          : "Selecione uma cor";
      }

      if (!variant) {
        if (slide.priceElement) slide.priceElement.textContent = "";
        if (slide.comparePriceElement) slide.comparePriceElement.hidden = true;
        return;
      }

      if (slide.priceElement) {
        slide.priceElement.textContent = this.formatMoney(Number(variant.price || 0));
      }

      if (slide.comparePriceElement) {
        const compareAtPrice = Number(variant.compare_at_price || 0);
        const price = Number(variant.price || 0);
        const hasCompareAtPrice = compareAtPrice > price;

        slide.comparePriceElement.hidden = !hasCompareAtPrice;

        if (hasCompareAtPrice) {
          slide.comparePriceElement.textContent = this.formatMoney(compareAtPrice);
        }
      }

      const image = variant.featured_image || displayCandidate?.product?.featured_image;
      const imageSource = this.getImageSource(image);

      if (imageSource) {
        const imageElement = this.ensureImageElement(slide);

        if (imageElement) {
          imageElement.src = imageSource;
          imageElement.alt =
            variant.name || displayCandidate?.product?.title || "Produto do conjunto";
        }
      }
    }


    getProductSectionUrl(candidate, variant = null) {
      const productUrl = candidate?.url;
      const sectionId = this.dataset.sectionId;

      if (!productUrl || !sectionId) return null;

      try {
        const url = new URL(productUrl, window.location.origin);

        if (variant?.id) {
          url.searchParams.set("variant", String(variant.id));
        }

        url.searchParams.set("section_id", sectionId);
        return url.toString();
      } catch (error) {
        console.error("[ECOMHARD Kits] URL de produto inválida.", error);
        return null;
      }
    }

    getProductMediaModal(documentRoot, sectionId) {
      return (
        documentRoot.getElementById?.(`ProductModal-${sectionId}`) ||
        documentRoot.querySelector?.(`product-modal[id="ProductModal-${sectionId}"]`) ||
        null
      );
    }

    async updateMainProductGallery(candidate, variant = null) {
      const sectionId = this.dataset.sectionId;
      const displayVariant =
        variant || this.getDisplayVariantFromCandidate(candidate);
      const contextKey = `${candidate?.id || ""}:${displayVariant?.id || ""}`;
      const sectionUrl = this.getProductSectionUrl(candidate, displayVariant);

      if (!candidate || !sectionId || !sectionUrl) return;

      const sameGalleryProduct =
        String(candidate.id) === String(this.currentGalleryProductId || "");
      const sameGalleryContext = contextKey === this.currentGalleryContextKey;

      if (sameGalleryProduct && sameGalleryContext) return;

      const currentGallery = document.getElementById(`MediaGallery-${sectionId}`);
      if (!currentGallery) return;

      const requestId = ++this.galleryRequestId;
      this.galleryAbortController?.abort();
      this.galleryAbortController = new AbortController();

      currentGallery.setAttribute("aria-busy", "true");
      this.classList.add("is-gallery-loading");

      try {
        let sectionHtml = this.galleryCache.get(sectionUrl);

        if (!sectionHtml) {
          const response = await fetch(sectionUrl, {
            method: "GET",
            headers: {
              Accept: "text/html",
              "X-Requested-With": "XMLHttpRequest",
            },
            cache: "no-store",
            signal: this.galleryAbortController.signal,
          });

          if (!response.ok) {
            throw new Error(`Não foi possível carregar a galeria (${response.status}).`);
          }

          sectionHtml = await response.text();
          this.galleryCache.set(sectionUrl, sectionHtml);
        }

        if (requestId !== this.galleryRequestId) return;

        const parsedDocument = new DOMParser().parseFromString(sectionHtml, "text/html");
        const nextGallery = parsedDocument.getElementById(`MediaGallery-${sectionId}`);

        if (!nextGallery) {
          throw new Error("A galeria do produto selecionado não foi encontrada.");
        }

        window.pauseAllMedia?.();

        const liveGallery = document.getElementById(`MediaGallery-${sectionId}`);
        if (!liveGallery) return;

        /*
         * O produto-base do kit pode não ter mídias próprias. Nesse caso,
         * o Liquid original adiciona product--no-media e remove a grade de
         * duas colunas. Ao carregar a mídia do produto escolhido, restauramos
         * explicitamente a coluna esquerda antes de atualizar a galeria.
         */
        this.ensureGalleryColumnLayout(liveGallery);

        /*
         * Mantém o mesmo <media-gallery> dentro da coluna original.
         * Assim, a grade principal da página nunca perde sua estrutura e a
         * galeria não passa para cima das informações do produto.
         */
        if (nextGallery) {
          liveGallery.innerHTML = nextGallery.innerHTML;
          liveGallery.dataset.desktopLayout =
            nextGallery.dataset.desktopLayout ||
            liveGallery.dataset.desktopLayout ||
            "";
          liveGallery.dataset.productId = String(candidate.id);

          const nextAriaLabel = nextGallery.getAttribute("aria-label");
          if (nextAriaLabel) liveGallery.setAttribute("aria-label", nextAriaLabel);

          if (typeof liveGallery.refresh === "function") {
            liveGallery.refresh();
          }

          const currentModal = this.getProductMediaModal(document, sectionId);
          const nextModal = this.getProductMediaModal(parsedDocument, sectionId);

          if (currentModal && nextModal) {
            currentModal.replaceWith(nextModal);
          } else if (currentModal && !nextModal) {
            currentModal.remove();
          } else if (!currentModal && nextModal) {
            const mainProduct = document.getElementById(`MainProduct-${sectionId}`);
            const pageWidth = mainProduct?.querySelector(".page-width");
            pageWidth?.append(nextModal);
          }

          this.currentGalleryProductId = String(candidate.id);
        }


        this.currentGalleryContextKey = contextKey;

        document.dispatchEvent(
          new CustomEvent("ecomhard-kit:gallery-updated", {
            detail: {
              source: "ecomhard-kits",
              productId: candidate.id,
              productHandle: candidate.handle,
              sectionId,
            },
          })
        );
      } catch (error) {
        if (error?.name === "AbortError") return;

        console.error("[ECOMHARD Kits] Não foi possível atualizar a galeria.", error);
      } finally {
        if (requestId === this.galleryRequestId) {
          document
            .getElementById(`MediaGallery-${sectionId}`)
            ?.removeAttribute("aria-busy");
          this.classList.remove("is-gallery-loading");
        }
      }
    }

    formatMoney(cents) {
      if (typeof window.formatMoney === "function") {
        return window.formatMoney(
          cents,
          window.FoxThemeSettings?.money_format || this.dataset.moneyFormat
        );
      }

      const amount = Number(cents || 0) / 100;
      const currency = window.Shopify?.currency?.active || "BRL";

      try {
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency,
        }).format(amount);
      } catch (error) {
        return `R$ ${amount.toFixed(2).replace(".", ",")}`;
      }
    }

    getAllSelectedVariants() {
      return this.slides.map((slide) => this.getSelectedVariant(slide));
    }

    getAllDisplayVariants() {
      return this.slides.map((slide) => this.getDisplayVariant(slide));
    }

    parseDiscountValue(rawValue) {
      let value = String(rawValue ?? "").trim();
      if (!value) return 0;

      value = value.replace(/[^\d,.-]/g, "");

      const commaIndex = value.lastIndexOf(",");
      const dotIndex = value.lastIndexOf(".");

      if (commaIndex > -1 && dotIndex > -1) {
        if (commaIndex > dotIndex) {
          value = value.replace(/\./g, "").replace(",", ".");
        } else {
          value = value.replace(/,/g, "");
        }
      } else if (commaIndex > -1) {
        value = value.replace(",", ".");
      }

      const parsedValue = Number.parseFloat(value);
      return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 0;
    }

    getDiscountConfig() {
      const type = this.normalize(this.dataset.kitDiscountType);
      const value = this.parseDiscountValue(this.dataset.kitDiscountValue);

      if (!value) return null;

      if (type.includes("porcent")) {
        return {
          type: "percentage",
          value: Math.min(value, 100),
        };
      }

      if (type.includes("fix")) {
        return {
          type: "fixed",
          value,
        };
      }

      return null;
    }

    getTotals() {
      const subtotal = this.getAllDisplayVariants().reduce(
        (sum, variant) => sum + Number(variant?.price || 0),
        0
      );
      const discount = this.getDiscountConfig();

      if (!discount || subtotal <= 0) {
        return {
          subtotal,
          discountAmount: 0,
          total: subtotal,
          discount,
        };
      }

      const rawDiscountAmount =
        discount.type === "percentage"
          ? Math.round((subtotal * discount.value) / 100)
          : Math.round(discount.value * 100);
      const discountAmount = Math.min(Math.max(rawDiscountAmount, 0), subtotal);

      return {
        subtotal,
        discountAmount,
        total: Math.max(subtotal - discountAmount, 0),
        discount,
      };
    }

    formatPercentage(value) {
      try {
        return new Intl.NumberFormat("pt-BR", {
          maximumFractionDigits: 2,
        }).format(Number(value || 0));
      } catch (error) {
        return String(value || 0).replace(".", ",");
      }
    }

    updateMainKitPrice({ subtotal, discountAmount, total }) {
      const priceRoot = document.querySelector(
        `#price-${this.dataset.sectionId} .price[data-ecomhard-kit-price="true"]`
      );

      if (!priceRoot) return;

      const hasDiscount = discountAmount > 0 && subtotal > total;
      const regularPrice = priceRoot.querySelector("[data-ecomhard-main-regular-price]");
      const comparePrice = priceRoot.querySelector("[data-ecomhard-main-compare-price]");
      const salePrice = priceRoot.querySelector("[data-ecomhard-main-sale-price]");
      const pixPrices = Array.from(
        priceRoot.querySelectorAll("[data-ecomhard-main-pix-price]")
      );
      const installmentRows = Array.from(
        priceRoot.querySelectorAll("[data-ecomhard-main-installment-row]")
      );
      const installmentQuantities = Array.from(
        priceRoot.querySelectorAll("[data-ecomhard-main-installment-quantity]")
      );
      const installmentPrices = Array.from(
        priceRoot.querySelectorAll("[data-ecomhard-main-installment-price]")
      );

      priceRoot.classList.toggle("price--on-sale", hasDiscount);
      priceRoot.classList.remove("price--sold-out");

      if (regularPrice) regularPrice.textContent = this.formatMoney(total);
      if (salePrice) salePrice.textContent = this.formatMoney(total);
      if (comparePrice) comparePrice.textContent = this.formatMoney(subtotal);

      const pixDiscountPercent = Math.min(
        Math.max(Number(priceRoot.dataset.ecomhardPixDiscountPercent || 0), 0),
        100
      );
      const calculatedPixPrice = Math.round(
        (total * (100 - pixDiscountPercent)) / 100
      );

      pixPrices.forEach((element) => {
        element.textContent = this.formatMoney(calculatedPixPrice);
      });

      const maxInstallments = Math.max(
        1,
        Number.parseInt(priceRoot.dataset.ecomhardInstallmentsQuantity || "1", 10) || 1
      );
      const minimumInstallmentValue = Math.max(
        0,
        Number.parseInt(priceRoot.dataset.ecomhardInstallmentMinValue || "0", 10) || 0
      );
      let finalInstallments = 1;

      for (let installment = 1; installment <= maxInstallments; installment += 1) {
        if (Math.floor(total / installment) >= minimumInstallmentValue) {
          finalInstallments = installment;
        }
      }

      const calculatedInstallmentPrice = Math.floor(total / finalInstallments);

      installmentRows.forEach((element) => {
        element.hidden = finalInstallments <= 1;
      });
      installmentQuantities.forEach((element) => {
        element.textContent = String(finalInstallments);
      });
      installmentPrices.forEach((element) => {
        element.textContent = this.formatMoney(calculatedInstallmentPrice);
      });

      priceRoot.dataset.ecomhardKitSubtotal = String(subtotal);
      priceRoot.dataset.ecomhardKitTotal = String(total);
    }

    updateTotal() {
      const totals = this.getTotals();
      const { subtotal, discountAmount, total, discount } = totals;

      if (this.subtotalElement) {
        this.subtotalElement.textContent = this.formatMoney(subtotal);
      }

      if (this.totalElement) {
        this.totalElement.textContent = this.formatMoney(total);
      }

      if (this.discountLabelElement) {
        if (discount && discountAmount > 0) {
          this.discountLabelElement.hidden = false;
          this.discountLabelElement.textContent =
            discount.type === "percentage"
              ? `-${this.formatPercentage(discount.value)}%`
              : `-${this.formatMoney(discountAmount)}`;
        } else {
          this.discountLabelElement.hidden = true;
          this.discountLabelElement.textContent = "";
        }
      }

      this.updateMainKitPrice(totals);
    }

    updateNavigation() {
      const currentSlide = this.slides[this.currentIndex];
      const currentVariant = this.getSelectedVariant(currentSlide);
      const isFirst = this.currentIndex === 0;
      const isLast = this.currentIndex === this.slides.length - 1;
      const canAddAll = this.getAllSelectedVariants().every(Boolean);

      if (this.counter) {
        this.counter.textContent = `Item ${this.currentIndex + 1} de ${this.slides.length}`;
      }

      if (this.previousButton) {
        this.previousButton.hidden = isFirst;
      }

      if (this.nextButton) {
        this.nextButton.hidden = isLast;
        this.nextButton.disabled = !currentVariant || this.isSubmitting;
      }

      if (this.addAllButton) {
        this.addAllButton.hidden = !isLast;
        this.addAllButton.disabled = !canAddAll || this.isSubmitting;
      }
    }

    goTo(index, { scroll = true, syncGallery = true } = {}) {
      if (index < 0 || index >= this.slides.length) return;

      this.currentIndex = index;
      this.slides.forEach((slide, slideIndex) => {
        const isCurrent = slideIndex === index;
        slide.element.hidden = !isCurrent;
        slide.element.setAttribute("aria-hidden", String(!isCurrent));
      });

      this.clearError();
      this.updateNavigation();

      if (syncGallery) {
        const activeCandidate = this.getActiveCandidate(this.slides[index]);

        if (activeCandidate) {
          const activeVariant =
            this.getSelectedVariantFromCandidate(activeCandidate) ||
            this.getDisplayVariantFromCandidate(activeCandidate);

          this.updateMainProductGallery(activeCandidate, activeVariant);
        }
      }

      if (scroll) {
        this.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }

    onChange(event) {
      const productChoice = event.target.closest("[data-kit-product-choice]");

      if (productChoice) {
        if (productChoice.disabled) return;

        const slideElement = productChoice.closest("[data-kit-slide]");
        const slide = this.slides.find((item) => item.element === slideElement);
        if (!slide) return;

        slide.selectedProductId = String(productChoice.dataset.productId || "");
        this.updateSlideDisplay(slide);
        this.updateTotal();
        this.updateNavigation();
        this.clearError();

        const activeCandidate = this.getActiveCandidate(slide);
        const activeVariant =
          this.getSelectedVariantFromCandidate(activeCandidate) ||
          this.getDisplayVariantFromCandidate(activeCandidate);

        this.updateMainProductGallery(activeCandidate, activeVariant);
        return;
      }

      const input = event.target.closest("[data-kit-option]");
      if (!input || input.disabled) return;

      const slideElement = input.closest("[data-kit-slide]");
      const candidateElement = input.closest("[data-kit-product-candidate]");
      const slide = this.slides.find((item) => item.element === slideElement);
      const candidate = slide?.candidates.find(
        (item) => item.element === candidateElement
      );

      if (!slide || !candidate) return;

      const position = String(input.dataset.optionPosition);
      candidate.selections.set(position, input.dataset.optionValue);
      this.reconcileSelections(candidate, position);
      this.syncAvailability(candidate);
      this.updateSlideDisplay(slide);
      this.updateTotal();
      this.updateNavigation();
      this.clearError();

      const selectedVariant =
        this.getSelectedVariantFromCandidate(candidate) ||
        this.getDisplayVariantFromCandidate(candidate);

      this.updateMainProductGallery(candidate, selectedVariant);
    }

    onPrevious() {
      this.goTo(this.currentIndex - 1);
    }

    onNext() {
      const currentVariant = this.getSelectedVariant(this.slides[this.currentIndex]);
      if (!currentVariant) return;
      this.goTo(this.currentIndex + 1);
    }

    getCartSectionIds() {
      const surfaceSections = this.cartSurface?.getSectionsToRender?.() || [];
      const mainCartSections = this.mainCart?.getSectionsToRender?.() || [];

      const ids = [
        ...surfaceSections.map((section) => section.id),
        ...mainCartSections.map((section) => section.section || section.id),
      ];

      if (!ids.length && this.cartDrawer) {
        ids.push("cart-drawer", "cart-icon-bubble");
      }

      return [...new Set(ids.filter(Boolean))];
    }

    getCartUrl() {
      return window.routes?.cart_url || "/cart";
    }

    async fetchCartState() {
      const cartUrl = this.getCartUrl();
      const stateUrl = cartUrl.endsWith(".js") ? cartUrl : `${cartUrl}.js`;

      const response = await fetch(stateUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(`Não foi possível consultar o carrinho (${response.status}).`);
      }

      return response.json();
    }

    async fetchCartSections(sectionIds) {
      if (!sectionIds.length) return {};

      const url = new URL(this.getCartUrl(), window.location.origin);
      url.searchParams.set("sections", sectionIds.join(","));
      url.searchParams.set("sections_url", window.location.pathname);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(
          `Não foi possível atualizar as seções do carrinho (${response.status}).`
        );
      }

      const sections = await response.json();

      if (!sections || typeof sections !== "object") {
        throw new Error("A resposta das seções do carrinho é inválida.");
      }

      return sections;
    }

    async fetchCartDrawerSectionsDirectly() {
      const cartUrl = this.getCartUrl();
      const sectionIds = ["cart-drawer", "cart-icon-bubble"];

      const responses = await Promise.all(
        sectionIds.map(async (sectionId) => {
          const url = new URL(cartUrl, window.location.origin);
          url.searchParams.set("section_id", sectionId);

          const response = await fetch(url.toString(), {
            method: "GET",
            headers: {
              Accept: "text/html",
              "X-Requested-With": "XMLHttpRequest",
            },
            cache: "no-store",
          });

          if (!response.ok) {
            throw new Error(
              `Não foi possível carregar a seção ${sectionId} (${response.status}).`
            );
          }

          return [sectionId, await response.text()];
        })
      );

      return Object.fromEntries(responses);
    }

    async refreshCartAfterAdd(addState, variants) {
      const sectionIds = this.getCartSectionIds();
      let sections =
        addState?.sections && typeof addState.sections === "object"
          ? addState.sections
          : {};

      /*
       * O Ajax Cart API já deve devolver as seções solicitadas junto com a
       * resposta de /cart/add.js. Caso algum tema/app remova essas seções,
       * fazemos uma segunda tentativa pela Section Rendering API.
       */
      if (!Object.keys(sections).length && sectionIds.length) {
        try {
          sections = await this.fetchCartSections(sectionIds);
        } catch (error) {
          console.warn(
            "[ECOMHARD Kits] Falha ao carregar as seções agrupadas do carrinho.",
            error
          );
        }
      }

      /*
       * Fallback específico e compatível com o cart drawer deste tema.
       * Usa o mesmo padrão de ?section_id=cart-drawer empregado pelo cart.js.
       */
      if (
        this.cartDrawer &&
        (!sections["cart-drawer"] || !sections["cart-icon-bubble"])
      ) {
        try {
          sections = {
            ...sections,
            ...(await this.fetchCartDrawerSectionsDirectly()),
          };
        } catch (error) {
          console.warn(
            "[ECOMHARD Kits] Falha ao carregar diretamente o cart drawer.",
            error
          );
        }
      }

      const renderState = {
        ...addState,
        id: Number(variants[0]?.id || addState?.items?.[0]?.id || 0),
        sections,
      };

      if (this.cartSurface?.renderContents && Object.keys(sections).length) {
        try {
          this.cartSurface.renderContents(renderState);
          return renderState;
        } catch (error) {
          console.warn(
            "[ECOMHARD Kits] O método nativo renderContents falhou. Tentando atualizar novamente.",
            error
          );

          if (this.cartDrawer) {
            try {
              const directSections =
                await this.fetchCartDrawerSectionsDirectly();

              const directState = {
                ...renderState,
                sections: {
                  ...sections,
                  ...directSections,
                },
              };

              this.cartDrawer.renderContents(directState);
              return directState;
            } catch (fallbackError) {
              console.warn(
                "[ECOMHARD Kits] A atualização alternativa do cart drawer também falhou.",
                fallbackError
              );
            }
          }
        }
      }

      const drawerItems = document.querySelector("cart-drawer-items");

      if (drawerItems?.onCartUpdate) {
        drawerItems.onCartUpdate();
        window.setTimeout(() => this.cartDrawer?.open?.(), 350);
        return renderState;
      }

      if (this.mainCart?.onCartUpdate) {
        this.mainCart.onCartUpdate();
        return renderState;
      }

      if (this.cartDrawer?.open) {
        this.cartDrawer.open();
        return renderState;
      }

      if (window.location.pathname === this.getCartUrl()) {
        window.location.reload();
      }

      return renderState;
    }

    publishCartUpdate(cartData, variants) {
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          originalSource: "ecomhard-kits",
          productVariantId: variants[0]?.id,
          variantId: variants[0]?.id,
          variantIds: variants.map((variant) => variant.id),
          cartData,
        });
      }

      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: {
            source: "ecomhard-kits",
            cartData,
            variantIds: variants.map((variant) => variant.id),
          },
        })
      );
    }

    async onAddAll() {
      const variants = this.getAllSelectedVariants();
      if (variants.some((variant) => !variant) || this.isSubmitting) return;

      this.setLoading(true);
      this.clearError();
      this.cartSurface?.setActiveElement?.(document.activeElement);

      const sectionIds = this.getCartSectionIds();
      const payload = {
        items: variants.map((variant) => ({
          id: Number(variant.id),
          quantity: 1,
        })),
      };

      /*
       * Solicita as seções do carrinho na própria requisição de adição.
       * É o mesmo fluxo usado pelo product-form.js nativo e evita abrir o
       * drawer com HTML anterior à inclusão dos produtos.
       */
      if (sectionIds.length) {
        payload.sections = sectionIds.join(",");
        payload.sections_url = window.location.pathname;
      }

      let productsWereAdded = false;

      try {
        const response = await fetch(
          this.dataset.cartAddUrl || window.routes?.cart_add_url || "/cart/add.js",
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(payload),
          }
        );

        const addState = await response.json();

        if (!response.ok || addState.status) {
          throw new Error(
            addState.description ||
              addState.message ||
              "Não foi possível adicionar os produtos ao carrinho."
          );
        }

        productsWereAdded = true;

        const renderState = await this.refreshCartAfterAdd(addState, variants);

        /*
         * A consulta do estado completo é útil para os eventos do tema, mas
         * não deve transformar uma adição bem-sucedida em erro visual caso
         * algum app bloqueie /cart.js.
         */
        let cartData = renderState;

        try {
          cartData = await this.fetchCartState();
        } catch (stateError) {
          console.warn(
            "[ECOMHARD Kits] Não foi possível consultar o estado completo do carrinho.",
            stateError
          );
        }

        this.publishCartUpdate(cartData, variants);
      } catch (error) {
        console.error("[ECOMHARD Kits]", error);

        if (productsWereAdded) {
          /*
           * Os itens já estão no carrinho. Não exibimos uma mensagem de erro
           * por uma falha secundária de atualização visual. Como última
           * tentativa, atualizamos o drawer nativo e o abrimos.
           */
          const drawerItems = document.querySelector("cart-drawer-items");
          drawerItems?.onCartUpdate?.();
          window.setTimeout(() => this.cartDrawer?.open?.(), 350);
        } else {
          this.showError(
            error.message || "Não foi possível adicionar os produtos ao carrinho."
          );
        }
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.isSubmitting = isLoading;
      this.classList.toggle("is-loading", isLoading);
      this.updateNavigation();
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.hidden = false;
      this.errorElement.textContent = message;
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.hidden = true;
      this.errorElement.textContent = "";
    }
  }

  customElements.define("ecomhard-kit-selector", EcomhardKitSelector);
}
